import {
  MissionAssessmentCreateDto,
  MissionAssessmentDraftCreateDto,
  MissionAssessmentDraftListItemDto,
  MissionAssessmentDraftPatchDto,
  MissionAssessmentDraftSelectDto,
  MissionAssessmentDraftsSelectDto,
  MissionAssessmentSelectDto,
} from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from './client';
import { queryKeys } from './keys';

/**
 * Hook to get all drafts for the current expert
 */
export const useGetMissionAssessmentDraftsForExpert = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MissionAssessmentDraftSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const result = await apiGet<MissionAssessmentDraftsSelectDto>({
        path: '/v1/expert/mission-assessment-drafts',
      });
      return result.missionAssessmentDrafts;
    },
    queryKey: enabled ? queryKeys.missionAssessmentDrafts.getDraftsForExpert : queryKeys.placeholders.default,
  });
};

/**
 * Hook to get a specific mission assessment draft by ID
 */
export const useGetMissionAssessmentDraft = (missionAssessmentDraftId?: string) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated && missionAssessmentDraftId !== undefined;

  return useQuery<MissionAssessmentDraftSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!missionAssessmentDraftId) {
        throw new Error('missionAssessmentDraftId is required to fetch mission assessment draft.');
      }
      return await apiGet<MissionAssessmentDraftSelectDto>({
        path: `/v1/expert/mission-assessment-drafts/${missionAssessmentDraftId}`,
      });
    },
    queryKey: enabled
      ? queryKeys.missionAssessmentDrafts.getDraft(missionAssessmentDraftId)
      : queryKeys.placeholders.default,
  });
};

/**
 * Hook to create a new mission assessment draft
 */
export const useCreateMissionAssessmentDraft = () => {
  const { apiPost } = useApi();

  return useMutation<MissionAssessmentDraftSelectDto, ApiErrorWrapper, MissionAssessmentDraftCreateDto>({
    mutationFn: async (data: MissionAssessmentDraftCreateDto) => {
      return await apiPost<MissionAssessmentDraftSelectDto>({
        path: '/v1/expert/mission-assessment-drafts',
        body: data,
      });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessmentDrafts.getDraftsForExpert,
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'missionAssessmentDrafts' && query.queryKey[1] === 'getDraftForTechnology',
      });
    },
    onError: (error) => {
      console.error('Error creating mission assessment draft:', error);
    },
  });
};

/**
 * Hook to update an existing mission assessment draft
 */
export const useUpdateMissionAssessmentDraft = (missionAssessmentDraftId?: string) => {
  const { apiPatch } = useApi();

  return useMutation<MissionAssessmentDraftSelectDto, ApiErrorWrapper, MissionAssessmentDraftPatchDto>({
    mutationFn: async (data: MissionAssessmentDraftPatchDto) => {
      if (!missionAssessmentDraftId) {
        throw new Error('missionAssessmentDraftId is required to update mission assessment draft.');
      }
      return await apiPatch<MissionAssessmentDraftSelectDto>({
        path: `/v1/expert/mission-assessment-drafts/${missionAssessmentDraftId}`,
        body: data,
      });
    },
    onSuccess: async (updatedDraft) => {
      if (missionAssessmentDraftId) {
        queryClient.setQueryData(queryKeys.missionAssessmentDrafts.getDraft(missionAssessmentDraftId), updatedDraft);
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessmentDrafts.getDraftsForExpert,
      });
    },
    onError: (error) => {
      console.error('Error updating mission assessment draft:', error);
    },
  });
};

/**
 * Hook to delete a mission assessment draft
 */
export const useDeleteMissionAssessmentDraft = () => {
  const { apiDelete } = useApi();

  return useMutation<void, ApiErrorWrapper, string>({
    mutationFn: async (missionAssessmentDraftId: string) => {
      await apiDelete({
        path: `/v1/expert/mission-assessment-drafts/${missionAssessmentDraftId}`,
      });
    },
    onSuccess: async (_, missionAssessmentDraftId) => {
      queryClient.removeQueries({
        queryKey: queryKeys.missionAssessmentDrafts.getDraft(missionAssessmentDraftId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessmentDrafts.getDraftsForExpert,
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'missionAssessmentDrafts' && query.queryKey[1] === 'getDraftForTechnology',
      });
    },
    onError: (error) => {
      console.error('Error deleting mission assessment draft:', error);
    },
  });
};

/**
 * Hook to publish a mission assessment draft as a complete mission assessment
 */
export const usePublishMissionAssessmentDraft = () => {
  const { apiPost } = useApi();

  return useMutation<
    MissionAssessmentSelectDto,
    ApiErrorWrapper,
    { missionAssessmentDraftId: string; missionAssessmentData: MissionAssessmentCreateDto }
  >({
    mutationFn: async (params: {
      missionAssessmentDraftId: string;
      missionAssessmentData: MissionAssessmentCreateDto;
    }) => {
      return await apiPost<MissionAssessmentSelectDto>({
        path: `/v1/expert/mission-assessment-drafts/${params.missionAssessmentDraftId}/publish`,
        body: params.missionAssessmentData,
      });
    },
    onSuccess: async (publishedAssessment, variables) => {
      const technologyId = publishedAssessment.technology.id;

      // Invalidate mission assessment queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessments.getAllForExpert,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessments.getVersionsByTechnology(technologyId),
      });

      // Invalidate mission assessment request queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessmentRequests.getMissionAssessmentRequests,
      });

      // Remove and invalidate draft queries
      queryClient.removeQueries({
        queryKey: queryKeys.missionAssessmentDrafts.getDraft(variables.missionAssessmentDraftId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessmentDrafts.getDraftsForExpert,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessmentDrafts.getDraftVersionsByTechnology(technologyId),
      });
    },
    onError: (error) => {
      console.error('Error publishing mission assessment draft:', error);
    },
  });
};

/**
 * Hook to get all draft versions for a technology by the current expert
 */
export const useGetMissionAssessmentDraftVersions = (technologyId?: string) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated && technologyId !== undefined;

  return useQuery<MissionAssessmentDraftListItemDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!technologyId) {
        throw new Error('technologyId is required to fetch draft versions.');
      }
      return await apiGet<MissionAssessmentDraftListItemDto[]>({
        path: `/v1/expert/mission-assessment-drafts/technology/${technologyId}/versions`,
      });
    },
    queryKey: enabled
      ? queryKeys.missionAssessmentDrafts.getDraftVersionsByTechnology(technologyId)
      : queryKeys.placeholders.default,
  });
};

/**
 * Hook to get all draft versions for a specific request by the current expert
 */
export const useGetMissionAssessmentDraftVersionsByRequest = (requestId?: string) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated && Boolean(requestId);

  return useQuery<MissionAssessmentDraftListItemDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!requestId) {
        throw new Error('requestId is required to fetch draft versions.');
      }
      return await apiGet<MissionAssessmentDraftListItemDto[]>({
        path: `/v1/expert/mission-assessment-drafts/request/${requestId}/versions`,
      });
    },
    queryKey: enabled
      ? queryKeys.missionAssessmentDrafts.getDraftVersionsByRequest(requestId as string)
      : queryKeys.placeholders.default,
  });
};
